import React, { useEffect, useMemo, useState } from 'react'
import { Form, Button, Container, Spinner } from 'react-bootstrap'
import { Footer, KonektoNavbar } from '../../components'
import '../../../src/App.scss'
import './MyCompanyProduct.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getMyCompany } from '../../store/Actions'
import { imageService } from '../../services/imageService'
import { toast } from 'react-toastify'
import { convertToSnakeCase, generateSlug } from '../../helpers'
import { productService } from '../../services/productService'
import { getMyCompanyProducts } from '../../store/Actions/Company'
import { FaTrashAlt } from 'react-icons/fa'
import Switch from 'react-switch'

export default function MyCompanyProduct() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const params = useParams()
  const productId = params?.proizvodId

  const { token } = useSelector(state => state.authentication)
  const { myCompany, products } = useSelector(state => state.company)

  const categories = useSelector(state => state.category.list)

  const [product, setProduct] = useState(
    productId ? products?.find(p => p.id == productId) : null,
  )

  const [formData, setFormData] = useState({
    title: product?.title ?? '',
    category: product?.category ?? '',
    subcategory: product?.subcategory ?? '',
    description: product?.description ?? '',
    price: product?.price ?? '',
    priceType: product?.price_type ?? 'fixed',
    minPrice: product?.min_price ?? '',
    maxPrice: product?.max_price ?? '',
    unit: product?.unit ?? '',
    minOrderQuantity: product?.min_order_quantity ?? '',
    webLink: product?.web_link ?? '',
    youtube: product?.youtube ?? '',
    isAvailable: product?.is_available ?? 1,
  })

  const [imagesToUpload, setImagesToUpload] = useState([])

  const [updateFormErrors, setUpdateFormErrors] = useState(null)
  const [updateResponseErrors, setUpdateResponseErrors] = useState(null)
  const [updateLoader, setUpdateLoader] = useState(false)

  const subcategories = useMemo(() => {
    const selectedCategory = categories?.find(c => c.id == formData.category)
    return selectedCategory?.children
  }, [formData?.category, categories])

  const handleChange = e => {
    const { name, value } = e.target

    let newFormData = { ...formData, [name]: value }

    if (name === 'category') {
      newFormData.subcategory = ''
    }

    setFormData(newFormData)
  }

  const handleSubmit = async e => {
    setUpdateFormErrors(null)
    e.preventDefault()

    let newErrors = {}

    if (formData?.title?.trim() === '') {
      newErrors = { ...newErrors, title: 'Naziv ne može biti prazan' }
    }

    if (product?.images && product.images.length + imagesToUpload.length > 3) {
      toast.error('Proizvod može imati 3 slike max')
      window.scrollTo({ top: 300, behavior: 'smooth' })
      return
    }

    if (Object.keys(newErrors).length > 0) {
      setUpdateFormErrors(newErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setUpdateLoader(true)

    // format keys from camelCase to snake_case
    const formattedData = convertToSnakeCase(formData)

    const data = new FormData()
    Object.keys(formattedData).forEach(key => {
      data.append(key, formattedData[key])
    })

    const titleSlug = generateSlug(formData.title)
    data.append('slug', titleSlug)
    data.append('company_id', myCompany?.id)

    if (imagesToUpload && imagesToUpload.length > 0) {
      imagesToUpload.forEach(file => {
        data.append('images[]', file)
      })
    }

    const response = product
      ? await productService.updateProduct(product.id, data)
      : await productService.addProduct(data)

    if (response.isOk && response.data) {
      dispatch(getMyCompanyProducts(myCompany.id))
      navigate('/moja-firma/proizvodi')
      toast.success('Proizvod uspješno sačuvan')
    } else {
      setUpdateResponseErrors(response.message)
    }

    setUpdateLoader(false)
  }

  // delete image from server
  const deleteImage = async imageId => {
    const response = await imageService.deleteImage(imageId)
    if (response.isOk && response.data) {
      const newProduct = {
        ...product,
        images: product.images.filter(i => i.id != imageId),
      }
      setProduct(newProduct)
    } else {
      toast.error(response.message || 'Desila se greška')
    }
  }

  useEffect(() => {
    if (!token) {
      navigate('/prijava')
      return
    }

    if (myCompany === null) {
      navigate('/registracija-firme')
      return
    }
  }, [token, myCompany, navigate])

  useEffect(() => {
    if (productId) {
      // editing mode
      // setFormData
    }
  }, [productId])

  useEffect(() => {
    if (token) {
      dispatch(getMyCompany())
    }
  }, [token])

  return (
    <div id="my-company-product">
      <KonektoNavbar />
      <Container className="my-5">
        <h3 className="fw-bold mb-4">
          {product ? product?.title : `Kreirajte novi proizvod ili uslugu`}
        </h3>

        <Form onSubmit={handleSubmit}>
          <div className="group-wrapper shadow">
            <h6 className="fw-bold mb-3">{`Osnovne informacije`}</h6>

            <Form.Group controlId="name" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">Naziv *</Form.Label>
                <div className="w-50">
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Npr. Prskalica za baštu"
                    value={formData.title}
                    onChange={handleChange}
                    maxLength={250}
                  />
                  {updateFormErrors?.title ? (
                    <span className="field-error">
                      {updateFormErrors?.title}
                    </span>
                  ) : null}
                </div>
              </div>
            </Form.Group>

            <Form.Group
              controlId="type"
              className="mb-3 d-flex align-items-center">
              <Form.Label className="w-50">Kategorija *</Form.Label>
              <Form.Select
                className="w-50"
                title="Kategorija"
                name="category"
                value={formData.category}
                onChange={handleChange}>
                <option key="default" value="" disabled>
                  Izaberite kategoriju
                </option>

                {categories.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {subcategories?.length > 0 ? (
              <Form.Group
                controlId="type"
                className="mb-3 d-flex align-items-center">
                <Form.Label className="w-50">Potkategorija</Form.Label>
                <Form.Select
                  className="w-50"
                  title="Potkategorija"
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleChange}>
                  <option key="default" value="" disabled>
                    Izaberite potkategoriju
                  </option>

                  {subcategories?.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}

            <Form.Group controlId="description" className="mb-3">
              <div className="d-flex align-items-top">
                <Form.Label className="w-50">Opis</Form.Label>
                <Form.Control
                  as="textarea"
                  className="w-50"
                  name="description"
                  placeholder="Opis proizvoda"
                  value={formData.description}
                  onChange={handleChange}
                  style={{ height: '100px' }} // Set the specific height for the textarea
                />
              </div>
            </Form.Group>

            <Form.Group controlId="is_available" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">Dostupan</Form.Label>
                <div className="w-50">
                  <Switch
                    checked={formData.isAvailable}
                    onChange={state =>
                      setFormData({ ...formData, isAvailable: state ? 1 : 0 })
                    }
                    height={28}
                    width={56}
                  />
                </div>
              </div>
            </Form.Group>
          </div>

          <div className="group-wrapper shadow">
            <h6 className="fw-bold mb-3">{`Mediji`}</h6>

            <Form.Group controlId="images" className="mb-3">
              <div className="d-flex align-items-start">
                <Form.Label className="w-50">Slike (Max. 3)</Form.Label>
                <div className="w-50">
                  <div className="custom-file-upload">
                    <input
                      type="file"
                      name="images"
                      id="image"
                      accept="image/*"
                      className="d-none"
                      multiple
                      onChange={e => {
                        setImagesToUpload([...e.target.files])
                      }}
                    />
                    <label
                      htmlFor="image"
                      className="btn btn-outline-secondary">
                      {imagesToUpload.length > 0
                        ? `Odabrano: ${imagesToUpload.length}`
                        : 'Izaberite slike'}
                    </label>
                  </div>

                  <div className="images-container">
                    {product?.images?.map(image => {
                      return (
                        <div className="image-div" key={image.id}>
                          <img src={image.path} />
                          <div
                            className="delete-div"
                            onClick={() => deleteImage(image.id)}>
                            <FaTrashAlt />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </Form.Group>

            <Form.Group controlId="youtube" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">Video link</Form.Label>
                <Form.Control
                  className="w-50"
                  type="text"
                  name="youtube"
                  placeholder="https://youtube.com/video-proizvoda"
                  value={formData.youtube}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>
          </div>

          <div className="group-wrapper shadow">
            <h6 className="fw-bold mb-3">{`Detalji za narudžbu`}</h6>

            <Form.Group controlId="price" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">Cijena *</Form.Label>

                <div className="w-50">
                  <div className="d-flex flex-column flex-md-row">
                    <Form.Check
                      type="radio"
                      label="Fiksna cijena"
                      name="priceType"
                      value="fixed"
                      defaultChecked
                      onChange={handleChange}
                      className="me-3"
                    />
                    <Form.Check
                      type="radio"
                      label="Promjenjiva cijena"
                      name="priceType"
                      value="variable"
                      onChange={handleChange}
                    />
                  </div>
                  {formData.priceType === 'fixed' ? (
                    <Form.Control
                      className="w-100 mt-3"
                      type="number"
                      name="price"
                      placeholder="Cijena u KM"
                      value={formData.price}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="d-flex mt-3">
                      <Form.Control
                        className="w-100"
                        type="number"
                        name="minPrice"
                        placeholder="Min. cijena u KM"
                        value={formData.minPrice}
                        onChange={handleChange}
                      />
                      <Form.Control
                        className="w-100 ms-2"
                        type="number"
                        name="maxPrice"
                        placeholder="Max. cijena u KM"
                        value={formData.maxPrice}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Form.Group>

            <Form.Group controlId="unit" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">Jedinica mjere</Form.Label>
                <Form.Control
                  className="w-50"
                  type="text"
                  name="unit"
                  placeholder="Npr. kg, cm..."
                  value={formData.unit}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>

            <Form.Group controlId="minOrderQuantity" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">
                  Minimalna količina za narudžbu
                </Form.Label>
                <Form.Control
                  className="w-50"
                  type="text"
                  name="minOrderQuantity"
                  placeholder="Npr. 100"
                  value={formData.minOrderQuantity}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>
          </div>

          <div className="group-wrapper shadow">
            <h6 className="fw-bold mb-3">{`Specifikacije`}</h6>

            <Form.Group controlId="webLink" className="mb-3">
              <div className="d-flex align-items-center">
                <Form.Label className="w-50">
                  Link na vašoj web stranici
                </Form.Label>
                <Form.Control
                  className="w-50"
                  type="text"
                  name="webLink"
                  placeholder="https://moja-web-stranica.com/proizvodi/moj-proizvod.html"
                  value={formData.webLink}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>
          </div>

          {updateResponseErrors ? (
            <p className="field-error text-center mb-0 mt-2">
              {updateResponseErrors}
            </p>
          ) : null}

          <div className="text-end">
            <Button
              disabled={updateLoader}
              variant="primary"
              type="submit"
              className="mt-3 rounded-pill primary-button">
              {updateLoader ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Sačuvaj i objavi'
              )}
            </Button>
          </div>
        </Form>
      </Container>
      <Footer />
    </div>
  )
}
